import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AlertController } from '@ionic/angular';
import Swal from 'sweetalert2';
import { Orders } from 'src/app/models/Orders.model';
import { Products } from 'src/app/models/Product.model';
import { Status } from 'src/app/models/status';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * Sub-modale "Conferma prodotti" — primo step del wizard picking.
 * Aperto via `OpenModalService.openCheck()` da preparazione-ordini-detaggli J.3.
 *
 * Cleanup v2.11.0 J.4.a:
 * - 🚨 FIX BUG: `getSimilarProducts()` chiamava l'API 3× di fila nella stessa
 *   funzione — 2 branch dead. Ora singola chiamata.
 * - 🚨 FIX BUG: `setRippleBackground()` DOM hack via ElementRef+Renderer2 dead.
 * - 🚨 FIX BUG: `presentAlert()` dead (mai chiamato).
 * - 🚨 FIX BUG: testo "caca" nel bottone HTML rimosso.
 * - 🚨 FIX BUG: `<app-product-detail-modal>` include verificato come dead
 *   (`isProdDetOn` mai settato qui — solo da gestione-prodotti).
 * - Switch a inject() + takeUntilDestroyed.
 * - Drop ~40 console.log ("Boss", "mata", "Merge?", "Barcode caca", "Bravo",
 *   "MINIMUM", "Orderus", "PRODOTTO IS", ecc.).
 * - Drop dead methods (selectSubProduct, seeValue, presentAlert,
 *   updateAllSpecialProductStates, checkAllSub).
 * - Drop kendo-dialog + kendo-dropdownlist + kendo-label + kendo-textbox +
 *   kendo-numerictextbox + ion-accordion + ion-item + ion-label refs.
 */
@Component({
  selector: 'app-confirm-products',
  templateUrl: './confirm-products.component.html',
  styleUrls: ['./confirm-products.component.scss'],
  standalone: false,
})


export class ConfirmProductsComponent implements OnInit {

  @Input() isProdCheck = true;
  @Output() modalClosed1 = new EventEmitter<void>();

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrdersService);
  private productService = inject(ProductsService);
  private alertController = inject(AlertController);
  public closeServ = inject(OpenModalService);

  // === State ===
  public order: Orders.Order | null = null;
  private buildOrderUpdate(): Orders.OrderUpdate {
    return new Orders.OrderUpdate(this.order!);
  }

  //public orderToUpdate: Orders.OrderUpdate | null = null;
  public productsToCheck: Products.IProductToSend[] = [];
  public similarProducts: Products.ProductSimilar[] = [];
  public productToAdd: Products.ProductToAdd | null = null;
  public selectedProduct: Products.Product | null = null;
  public selectedProductIdToDelete: any = null;
  public clickedProduct = '';
  public barcodes: any = null;

  public id = '';
  public filterTerm: string | undefined;
  public quantity: number | null = null;
  public pr_note = '';

  public isDDT = false;
  public isSimilar = false;
  public isAnyChecked = false;
  public searchBool = false;
  public openedSearch = false;
  public areAllSubProductsChecked = false;
  public firstEnteredOnPage = true;

  public activeAccordion: string | null = null;
  public numbers: number[] = [];
  public stepCount = 0;
  private initialized = false;

  private cdr = inject(ChangeDetectorRef);
  ionViewWillEnter(): void {
    this.getProducts();
  }

  // === Lifecycle ===
  ngOnInit(): void {
    this.firstEnteredOnPage = true;
    this.getProducts();
  }

  close(): void {
    this.closeServ.closeCheck();
  }

  // === Data load ===
  getProducts(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';

    this.orderService
      .getOrder1(+this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;

        this.order = null; 

        setTimeout(() => {
          this.order = {
            ...result,
            products: (result.products ?? []).map(product => {
              const baseQty = Number(product.op_qta || 0);

              return {
                ...product,
                sub_products: product.sub_products?.map(sub => ({
                  ...sub,
                  minimumPerProduct: sub.osp_qty / (baseQty || 1),
                  osp_qty_picked: sub.osp_qty_picked ?? 0,
                }))
              };
            })
          } as Orders.Order;

          this.checkSelectedAll();
          this.cdr.detectChanges();
        });
      });
  }

  // === Selection logic ===
  selectAll(): void {
    if (!this.order) return;
    this.order.products.forEach(p => {
      p.op_checked = true;
      p.op_qta_picked = p.op_qta;

      p.sub_products?.forEach(s => {
        s.osp_checked = true;
        s.osp_qty_picked = s.osp_qty_ordered;
      });
    });
    this.checkSelectedAll();
  }

  opChecked(product: Products.IProductToSend, index: number): void {
    if (this.isDDT || !this.order) return;
    this.order.products[index].op_checked = true;
    this.checkSelectedAll();
    this.closeServ.isColorConfirmed();
  }

  /**
   * Toggle accordion sub-products. Ionic ion-accordion-group nasce
   * per accordion uno-alla-volta — preserviamo il behavior.
   */
  onAccordionChange(event: any): void {
    this.activeAccordion = event?.detail?.value ?? null;
  }

  handleToggle(event: Event, value: string) {
    const details = event.target as HTMLDetailsElement;
    this.onAccordionChange({
      detail: {
        value: details.open ? value : null
      }
    });
  }

  // === Quantity update ===
  updateProduct(value: number, i: number): void {
    if (!this.order) return;
    const product = this.order.products[i];

    const qty = Number(value || 0);
    product.op_qta_picked = qty;

    product.sub_products?.forEach((sub) => {
      sub.osp_qty_picked = (sub.minimumPerProduct ?? 0) * qty;
      sub.osp_checked = true;
    });

    product.op_checked = true;
    const payload = this.buildOrderUpdate();

    this.orderService.updateOrder(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!res) return;
          this.checkSelectedAll();
        }
      });
  }

  /**
   * Quantità disponibili per il dropdown (1..N). Pre-computed evita
   * di rebuildare ad ogni click.
   */
  changeProductNumber(n: number): void {
    this.numbers = Array.from({ length: n }, (_, index) => index + 1);
  }

  // === Sub-products ===

  /**
   * Toggle sub-product picked. Verifica se tutti i sub-products del
   * product parent sono ora "checked" → marca anche il parent come
   * `op_checked`.
   */
  checkIfSubProductsArePresent(
    product: Products.Product,
    index: number,
    subProduct: Products.SubProducts,
  ): boolean {
    const sub = product.sub_products[index];
    sub.osp_checked = !sub.osp_checked;

    const allChecked = product.sub_products.every(
      (s) => s.osp_checked === true
    );
    product.op_checked = allChecked;
    this.areAllSubProductsChecked = allChecked;

    return allChecked;
  }

  // === Check completion ===
  checkSelectedAll(): void {
    if (!this.order) return;

    const stepFrw = 1;
    const stepBck = 0;
    const allChecked = this.order.products.every((p) => p.op_checked === true);

    if (allChecked) {
      this.order.stepCount = stepFrw;
      this.orderService.pages.next(stepFrw);
    } 
    else {
      this.order.stepCount = stepBck;
      this.orderService.pages.next(stepBck);
    }
  }

  // === Save step ===
  saveStep(pageName: string): void {
    if (!this.order) return;

    const statusNo =
      pageName === 'products'
        ? Status.StatusesUser.step1
        : 0;

    const payload = new Orders.OrderUpdate(this.order);

    payload.products = (payload.products ?? []).map(p => ({
      ...p,
      op_checked: true,
    }));

    this.orderService
      .updateOrder(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkSelectedAll();

        this.orderService
          .setStatus(this.order!.order_id, statusNo)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.modalClosed1.emit();
            this.close();

            Swal.fire({
              icon: 'success',
              title: 'Aggiunti!',
              text: 'I prodotti selezionati sono stati aggiunti con successo.',
              timer: 1500,
              timerProgressBar: true,
            });
          });
      });
  }

  // === Product details + QR popovers ===
  goToProductDetails(product: any): void {
    this.clickedProduct = product;
    this.closeServ.prodDetOn();
    this.checkSelectedAll();
  }

  seeQrCodes(barcode: any, _order: any): void {
    this.barcodes = barcode;
    this.closeServ.seeQRON();
  }

  seeQrCodesSub(barcode: any, _order: any): void {
    this.barcodes = barcode;
    this.closeServ.seeQrSubOn();
  }

  hasValidBarcodes(prod: any): boolean {
    return prod?.barcodes?.some((bar: any) => bar.barcode_text !== null) ?? false;
  }

  hasValidBarcodesSub(sub: any): boolean {
    return sub?.barcodes?.some((bar: any) => bar.barcode_text !== null) ?? false;
  }

  // === Replace / delete product ===

  selectProduct(object: Products.Product, i: number): void {
    if (!this.order) return;
    this.isSimilar = true;
    this.selectedProduct = object;
    this.filterTerm = this.selectedProduct.op_description;
    this.selectedProductIdToDelete = object.products_order_id;
    this.order.products[i].op_checked = true;
    this.searchBool = !this.searchBool;
    this.openedSearch = !this.openedSearch;
    this.getSimilarProducts();
  }

  /**
   * 🚨 FIX BUG: legacy chiamava l'API 3 volte di fila nella stessa funzione
   * (`if (searchBool)` + `if (!filterTerm)` + chiamata finale unconditional).
   * Ora una sola chiamata.
   */
  getSimilarProducts(): void {
    this.orderService
      .getSimilarProducts(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: IWsResult) => {
        if (!res?.success) {
          this.similarProducts = [];
          this.cdr.detectChanges();
          return;
        }
        this.similarProducts = res.data;
        this.productToAdd = res.data;
        this.searchBool = false;
        this.cdr.detectChanges();
      });
  }

  addNewProduct(): void {
    this.isSimilar = false;
    this.openedSearch = true;
    this.getSimilarProducts();
  }

  addProduct(): void {
    if (!this.order || !this.productToAdd) return;

    this.productToAdd.order_id = this.order.order_id;

    if (this.isSimilar && this.selectedProduct) {
      this.productToAdd.existingProduct(this.selectedProduct);
    } 
    else {
      this.productToAdd.op_remaining_qty = `${this.quantity ?? 0}`;
      this.productToAdd.op_qty = this.quantity ?? 0;
      this.productToAdd.op_note = this.pr_note;
    }

    this.productService.getProduct(this.productToAdd.op_code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          Swal.fire({
            icon: 'error',
            title: 'Errore!',
            text: 'Il prodotto cercato non esiste.',
              timer: 1500,
              timerProgressBar: true,
          });
          return;
        }
        this.productToAdd!.addedProduct(result);

        this.orderService.addProductToOrder(this.productToAdd!)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            if (this.isSimilar) this.deleteProduct();
            this.openedSearch = false;
            this.getProducts();
            this.cdr.detectChanges();
          });
      });
  }

  deleteProduct(object?: Products.Product, _i?: number): void {
    if (object !== undefined) {
      this.selectedProductIdToDelete = object.products_order_id;
    }
    this.orderService
      .deleteProductFromOrder(this.selectedProductIdToDelete)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.success) {
          this.getProducts();
          Swal.fire({
            icon: 'success',
            title: 'Eliminato!',
            text: 'Prodotto rimosso con successo.',
            timer: 1500,
            timerProgressBar: true,
          });
        } 
        else {
          Swal.fire({
            icon: 'error',
            title: 'Errore!',
            text: 'Il prodotto selezionato non può essere rimosso.',
            timer: 1500,
            timerProgressBar: true,
          });
        }
      });
  }

  selectProductToAdd(i: number): void {
    const product = this.similarProducts[i];
    this.similarProducts.forEach((p) => (p.pr_checked = false));

    product.pr_checked = !product.pr_checked;
    this.isAnyChecked = product.pr_checked;

    if (product.pr_checked) {
      this.productToAdd = new Products.ProductToAdd(product);
    }
  }
  
}
